import type { ApiResponse, PaginatedResponse } from './types'
import { API_BASE_URL, SOCKET_URL } from '../config'

// Debug configuration - set to false to reduce logging
const API_DEBUG_ENABLED = false
const debug = API_DEBUG_ENABLED ? console.debug.bind(console, '[API DEBUG]') : () => {}

console.log(`Iris Base URL: ${API_BASE_URL}`)
console.log(`Iris Socket URL: ${SOCKET_URL}`)

const DEFAULT_TIMEOUT = 10000 // 10 seconds
const RETRY_ATTEMPTS = 3
const RETRY_DELAY = 1000 // 1 second

// Network delay simulation for testing
const NETWORK_DELAYS = {
  fast: 100,      // 100ms - cached/fast responses
  medium: 400,    // 400ms - normal API responses
  slow: 800,      // 800ms - complex operations
  timeout: 2000   // 2s - slow network simulation
}

export class ApiClient {
  private timeout: number
  private accessToken: string | null = null

  constructor(timeout = DEFAULT_TIMEOUT) {
    this.timeout = timeout
    debug(`ApiClient constructor - using Iris backend: ${API_BASE_URL}`)
  }

  // Method to set the access token for authenticated requests
  setAccessToken(token: string | null) {
    this.accessToken = token
  }

  // Method to get the current access token
  getAccessToken(): string | null {
    return this.accessToken
  }

  public getSocketUrl(): string {
    return SOCKET_URL
  }

  // Simulate network delay for testing
  private async simulateDelay(type: keyof typeof NETWORK_DELAYS = 'medium'): Promise<void> {
    const delay = NETWORK_DELAYS[type]
    const jitter = Math.random() * 100 // Add some jitter
    await new Promise(resolve => setTimeout(resolve, delay + jitter))
  }

  // Generic API request method
  async request<T>(
    endpoint: string,
    options: {
      method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
      data?: any
      delay?: keyof typeof NETWORK_DELAYS
      headers?: Record<string, string>
      params?: Record<string, string | number | boolean | string[]>
    } = {}
  ): Promise<T> {
    const {
      method = 'GET',
      data,
      delay = 'medium',
      headers = {},
      params
    } = options

    // Build URL with query parameters
    let url = `${API_BASE_URL}${endpoint}`
    if (params) {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => searchParams.append(key, String(v)))
          } else {
            searchParams.append(key, String(value))
          }
        }
      })
      if (searchParams.toString()) {
        url += `?${searchParams.toString()}`
      }
    }

    // Simulate network delay for testing
    await this.simulateDelay(delay)

    // Log API calls in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] ${method} ${url}`, data ? { data } : '')
    }

    debug(`Making request to: ${url}`)

    const requestOptions: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(this.accessToken && { Authorization: `Bearer ${this.accessToken}` }),
        ...headers
      },
      credentials: 'include' // Include cookies for refresh token
    }

    if (data && method !== 'GET') {
      requestOptions.body = JSON.stringify(data)
    }

    debug(`Making fetch request with options:`, requestOptions)
    const response = await fetch(url, requestOptions)
    debug(`Fetch response:`, response.status, response.statusText)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw ApiError.fromResponse({
        status: response.status,
        statusText: response.statusText,
        data: errorData
      })
    }
    if (response.status === 204) return {} as T
    return await response.json()
  }

  // Convenience methods
  async get<T>(endpoint: string, options?: {
    delay?: keyof typeof NETWORK_DELAYS
    params?: Record<string, string | number | boolean | string[]>
  }): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', ...options })
  }

  async post<T>(endpoint: string, data: any, options?: {
    delay?: keyof typeof NETWORK_DELAYS
    params?: Record<string, string | number | boolean | string[]>
  }): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', data, ...options })
  }

  async put<T>(endpoint: string, data: any, options?: {
    delay?: keyof typeof NETWORK_DELAYS
    params?: Record<string, string | number | boolean | string[]>
  }): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', data, ...options })
  }

  async patch<T>(endpoint: string, data: any, options?: {
    delay?: keyof typeof NETWORK_DELAYS
    params?: Record<string, string | number | boolean | string[]>
  }): Promise<T> {
    return this.request<T>(endpoint, { method: 'PATCH', data, ...options })
  }

  async delete<T>(endpoint: string, options?: {
    delay?: keyof typeof NETWORK_DELAYS
    params?: Record<string, string | number | boolean | string[]>
  }): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', ...options })
  }
}

// Custom API Error class
export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
    public details?: Record<string, any>
  ) {
    super(message)
    this.name = 'ApiError'
  }

  static fromResponse(response: { status: number; statusText: string; data?: any }): ApiError {
    return new ApiError(
      response.data?.message || response.statusText || 'An error occurred',
      response.data?.code || 'UNKNOWN_ERROR',
      response.status,
      response.data
    )
  }
}

// Network Errors
export class NetworkError extends Error {
  constructor(message: string = 'Network request failed') {
    super(message)
    this.name = 'NetworkError'
  }
}

export class TimeoutError extends Error {
  constructor(message: string = 'Request timed out') {
    super(message)
    this.name = 'TimeoutError'
  }
}

// Retry wrapper for API calls
export async function withRetry<T>(
  apiCall: () => Promise<T>,
  maxAttempts = RETRY_ATTEMPTS,
  delay = RETRY_DELAY
): Promise<T> {
  let lastError: Error

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await apiCall()
    } catch (error) {
      lastError = error as Error

      // Don't retry on client errors (4xx) except for 408 (timeout) and 429 (rate limit)
      if (error instanceof ApiError) {
        const shouldRetry = error.status >= 500 || error.status === 408 || error.status === 429
        if (!shouldRetry) {
          throw error
        }
      }

      // If this was the last attempt, throw the error
      if (attempt === maxAttempts) {
        break
      }

      // Wait before retrying (with exponential backoff)
      const backoffDelay = delay * Math.pow(2, attempt - 1) + Math.random() * 1000
      await new Promise(resolve => setTimeout(resolve, backoffDelay))
    }
  }

  throw lastError
}

// Create a default API client instance
export const apiClient = new ApiClient()

// Export socket URL for WebSocket connections
export { SOCKET_URL as IRIS_SOCKET_URL }

// Helper to get current backend info
export function getCurrentBackend(): { type: string; url: string } {
  return {
    type: 'iris',
    url: API_BASE_URL
  }
}

// Helper function to handle API responses
export function handleApiResponse<T>(response: ApiResponse<T>): T {
  if (!response.success) {
    throw new ApiError(
      response.error || response.message || 'API request failed',
      'API_ERROR',
      400
    )
  }
  return response.data
}

// Helper function for paginated responses
export function handlePaginatedResponse<T>(response: any): {
  data: T[]
  pagination: { page: number; limit: number; total: number; totalPages: number }
} {
  if (!response.success) {
    throw new ApiError(
      response.error || response.message || 'API request failed',
      'API_ERROR',
      400
    )
  }
  return {
    data: response.data,
    pagination: response.pagination
  }
}

// Convenience API wrapper with standard response handling
export const api = {
  // Generic API calls that return the full response
  get: <T>(endpoint: string, params?: Record<string, string | number | boolean | string[]>) =>
    apiClient.get<ApiResponse<T>>(endpoint, { params }),

  post: <T>(endpoint: string, data?: unknown, params?: Record<string, string | number | boolean | string[]>) =>
    apiClient.post<ApiResponse<T>>(endpoint, data, { params }),

  put: <T>(endpoint: string, data?: unknown, params?: Record<string, string | number | boolean | string[]>) =>
    apiClient.put<ApiResponse<T>>(endpoint, data, { params }),

  patch: <T>(endpoint: string, data?: unknown, params?: Record<string, string | number | boolean | string[]>) =>
    apiClient.patch<ApiResponse<T>>(endpoint, data, { params }),

  delete: <T>(endpoint: string, params?: Record<string, string | number | boolean | string[]>) =>
    apiClient.delete<ApiResponse<T>>(endpoint, { params }),

  // Paginated API calls
  getPaginated: <T>(endpoint: string, params?: Record<string, string | number | boolean | string[]>) =>
    apiClient.get<PaginatedResponse<T>>(endpoint, { params }),
}